// Extracted from tasks.tsx — task list view components
// These are presentational/structural components used by TasksPage.
import { useQuery, useMutation } from "@tanstack/react-query";
import { assignedTaskCardClass, isAssignedToAssistant } from "@/components/tasks/assigned-task-highlight";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { CheckSquare, Square, Calendar, Link as LinkIcon, Trash2, Pencil, Plus, Search, ListTodo, Target, BookOpen, Milestone, Filter, FileText, List, LayoutGrid, MoreVertical, Star, ChevronDown, ChevronRight, GripVertical, Tag, AlertCircle, Clock, Share2, Users, UserPlus, File, Download, X, Sparkles, Flag, ExternalLink, Paperclip, MessageSquare, Activity, Eye, EyeOff, CalendarPlus, Upload, HardDrive, FolderOpen } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { format, isPast, parseISO, endOfDay, startOfDay } from "date-fns";
import { formatDateInput, parseLocalDate } from "@/lib/dateUtils";
import CreateNoteModal from "@/components/create-note-modal";
import KanbanBoard from "@/components/kanban-board";
import KanbanColumnDialog from "@/components/kanban-column-dialog";
import ShareDialog from "@/components/share-dialog";
import DocumentUploadButton from "@/components/document-upload-button";
import { useAuth } from "@/hooks/useAuth";
import { useTaskFilters } from "@/hooks/useTaskFilters";
import { useSharedTasks, useUpdateSharedTask } from "@/lib/queries/community";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { TaskAttachmentsDialog } from "@/components/tasks/task-attachments";
import { InlineSubtaskPanel, AddMilestoneButton } from "@/components/tasks/subtask-components";
import { sanitizeCategory } from "@/lib/categoryUtils";
import type { Task } from "@/components/tasks/task-types";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';


export function SortableTaskItem({ task, isOverdue, getLinkedItemName, getSubTaskProgress, toggleTaskMutation, toggleTopThreeMutation, setViewingTask, setCreateNoteForTask, setEditingTask, setDeletingTaskId, selectedTasks, toggleTaskSelection, onShareTask, quickProgressPopoverId, setQuickProgressPopoverId, quickProgressPopoverNote, setQuickProgressPopoverNote, quickProgressMutation, showSubtasks, taskSubtasks, onSubtaskToggle, onSubtaskStar, onSubtaskDateChange, onSubtaskTextChange, onSubtaskReorder, onSubtaskDelete, hideCompleted, onToggleHideCompleted, onScheduleTask, highlightAssigned = false }: any) {
  const [quickAddSubtaskOpen, setQuickAddSubtaskOpen] = useState(false);
  const [quickAddSubtaskText, setQuickAddSubtaskText] = useState("");
  const [localExpanded, setLocalExpanded] = useState(false);

  const quickAddSubtaskMutation = useMutation({
    mutationFn: async (text: string) => {
      const response = await apiRequest("POST", `/api/tasks/${task.id}/subtasks`, { text });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      setQuickAddSubtaskText("");
      setQuickAddSubtaskOpen(false);
    },
  });

  const handleQuickAddSubtask = () => {
    if (quickAddSubtaskMutation.isPending) return;
    const text = quickAddSubtaskText.trim();
    if (text) quickAddSubtaskMutation.mutate(text);
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Card
        className={`transition-all hover:shadow-md cursor-pointer ${
          highlightAssigned ? assignedTaskCardClass : isOverdue(task) ? "border-red-300 bg-red-50 dark:bg-red-950/20" : ""
        } ${selectedTasks.has(task.id) ? "ring-2 ring-primary" : ""}`}
        data-testid={`task-card-${task.id}`}
        onClick={() => setEditingTask(task)}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div {...listeners} className="cursor-grab active:cursor-grabbing mt-1" onClick={(e) => e.stopPropagation()}>
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="mt-1 flex items-center" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={selectedTasks.has(task.id)}
                onChange={() => toggleTaskSelection(task.id)}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer accent-primary"
                data-testid={`checkbox-select-task-${task.id}`}
              />
            </div>
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={task.completed}
                onCheckedChange={() => toggleTaskMutation.mutate({ id: task.id, completed: !task.completed })}
                className="mt-1"
                data-testid={`checkbox-task-${task.id}`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0 flex items-center gap-3">
                  <h3
                    className={`font-medium text-foreground cursor-pointer hover:text-primary transition-colors flex-shrink-0 ${
                      task.completed ? "line-through text-muted-foreground" : ""
                    }`}
                    onClick={() => setEditingTask(task)}
                    data-testid={`task-title-${task.id}`}
                  >
                    {task.title}
                  </h3>
                  {task.isShared && task.sharedBy && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                      <Users className="h-3 w-3" />
                      Shared by {task.sharedBy.firstName}
                    </span>
                  )}
                  {getSubTaskProgress && (() => {
                    const progress = getSubTaskProgress(task.id);
                    if (progress && progress.total > 0) {
                      return (
                        <div className="flex-1 max-w-[200px] flex items-center gap-2" data-testid={`task-progress-bar-${task.id}`}>
                          <Progress value={progress.percentage} className="h-2" />
                          <span className="text-xs text-muted-foreground whitespace-nowrap">{progress.percentage}%</span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  {/* Always visible: Add to Calendar button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onScheduleTask) onScheduleTask(task);
                    }}
                    data-testid={`button-schedule-task-${task.id}`}
                    title="Add to Calendar"
                  >
                    <CalendarPlus className="h-4 w-4" />
                  </Button>
                  {/* Desktop: show all buttons */}
                  <div className="hidden md:flex items-center gap-1">
                    {showSubtasks && (
                      <Popover open={quickAddSubtaskOpen} onOpenChange={(open) => { setQuickAddSubtaskOpen(open); if (!open) setQuickAddSubtaskText(""); }}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            title="Add Sub-task"
                            data-testid={`button-quick-add-subtask-${task.id}`}
                          >
                            <ListTodo className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72" onClick={(e) => e.stopPropagation()}>
                          <div className="space-y-2">
                            <p className="text-sm font-medium">Add Sub-task</p>
                            <Input
                              autoFocus
                              placeholder="Sub-task title..."
                              value={quickAddSubtaskText}
                              onChange={(e) => setQuickAddSubtaskText(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleQuickAddSubtask(); } }}
                              data-testid={`input-quick-add-subtask-${task.id}`}
                            />
                            <Button
                              size="sm"
                              className="w-full"
                              onClick={handleQuickAddSubtask}
                              disabled={!quickAddSubtaskText.trim() || quickAddSubtaskMutation.isPending}
                              data-testid={`button-submit-quick-add-subtask-${task.id}`}
                            >
                              {quickAddSubtaskMutation.isPending ? "Adding..." : "Add"}
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                    {showSubtasks && !(task.isShared && task.sharedPermission !== "edit") && <AddMilestoneButton taskId={task.id} />}
                    <Popover 
                      open={quickProgressPopoverId === task.id} 
                      onOpenChange={(open) => {
                        setQuickProgressPopoverId(open ? task.id : null);
                        if (!open) setQuickProgressPopoverNote("");
                      }}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          data-testid={`button-quick-progress-${task.id}`}
                          title="Add Quick Progress"
                        >
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80" onClick={(e) => e.stopPropagation()}>
                        <div className="space-y-3">
                          <div className="font-medium text-sm">Quick Progress for: {task.title}</div>
                          <Textarea
                            placeholder="Enter progress notes..."
                            value={quickProgressPopoverNote}
                            onChange={(e) => setQuickProgressPopoverNote(e.target.value)}
                            className="min-h-[80px]"
                            data-testid={`textarea-quick-progress-${task.id}`}
                          />
                          <Button
                            size="sm"
                            className="w-full"
                            onClick={() => {
                              if (quickProgressPopoverNote.trim()) {
                                quickProgressMutation.mutate({
                                  taskId: task.id,
                                  notes: quickProgressPopoverNote.trim(),
                                });
                              }
                            }}
                            disabled={!quickProgressPopoverNote.trim() || quickProgressMutation.isPending}
                            data-testid={`button-submit-quick-progress-${task.id}`}
                          >
                            {quickProgressMutation.isPending ? "Saving..." : "Add Progress"}
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <TaskAttachmentsDialog
                      taskId={task.id}
                      trigger={
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          data-testid={`button-view-documents-${task.id}`}
                          title="View Documents"
                        >
                          <Paperclip className="h-4 w-4" />
                        </Button>
                      }
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleTopThreeMutation.mutate(task.id);
                      }}
                      data-testid={`button-toggle-top-three-${task.id}`}
                      title={task.isTopThree ? "Remove from Top Three" : "Add to Top Three"}
                    >
                      <Star 
                        className="h-4 w-4" 
                        fill={task.isTopThree ? "#f59e0b" : "none"}
                        style={{ color: task.isTopThree ? "#f59e0b" : "currentColor" }}
                      />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCreateNoteForTask(task);
                      }}
                      data-testid={`button-create-note-task-${task.id}`}
                      title="Create Note"
                    >
                      <FileText className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        onShareTask(task);
                      }}
                      data-testid={`button-share-task-${task.id}`}
                      title="Share Task"
                    >
                      <Share2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingTask(task);
                      }}
                      data-testid={`button-edit-task-${task.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeletingTaskId(task.id);
                      }}
                      data-testid={`button-delete-task-${task.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  {/* Mobile: dropdown menu */}
                  <div className="md:hidden">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`button-task-actions-mobile-${task.id}`}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onClick={() => setEditingTask(task)} data-testid={`mobile-edit-task-${task.id}`}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleTopThreeMutation.mutate(task.id)} data-testid={`mobile-star-task-${task.id}`}>
                          <Star className="h-4 w-4 mr-2" fill={task.isTopThree ? "#f59e0b" : "none"} />
                          {task.isTopThree ? "Unstar" : "Star"}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setCreateNoteForTask(task)} data-testid={`mobile-note-task-${task.id}`}>
                          <FileText className="h-4 w-4 mr-2" />
                          Create Note
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onShareTask(task)} data-testid={`mobile-share-task-${task.id}`}>
                          <Share2 className="h-4 w-4 mr-2" />
                          Share
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { if (onScheduleTask) onScheduleTask(task); }} data-testid={`mobile-schedule-task-${task.id}`}>
                          <CalendarPlus className="h-4 w-4 mr-2" />
                          Add to Calendar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDeletingTaskId(task.id)} className="text-destructive" data-testid={`mobile-delete-task-${task.id}`}>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
              {task.description && (
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2" data-testid={`task-description-${task.id}`}>
                  {task.description}
                </p>
              )}
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <div className="flex items-center gap-1 text-xs text-muted-foreground" data-testid={`task-category-${task.id}`}>
                  <Tag className="h-3 w-3" />
                  {sanitizeCategory(task.category) || "Uncategorized"}
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground" data-testid={`task-priority-${task.id}`}>
                  <AlertCircle className="h-3 w-3" />
                  Priority: {task.priority || "medium"}
                </div>
                {task.dueDate && (
                  <div className={`flex items-center gap-1 text-xs ${
                    isOverdue(task) ? "text-red-600 font-medium" : "text-muted-foreground"
                  }`} data-testid={`task-due-date-${task.id}`}>
                    <Calendar className="h-3 w-3" />
                    Due: {format(new Date(task.dueDate), "MMM d, yyyy")}
                    {isOverdue(task) && <span className="ml-1">(Overdue)</span>}
                  </div>
                )}
                {getLinkedItemName && getLinkedItemName(task) && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground" data-testid={`task-link-${task.id}`}>
                    <LinkIcon className="h-3 w-3" />
                    <span>{getLinkedItemName(task)?.type}:</span>
                    <span className="font-medium">{getLinkedItemName(task)?.name}</span>
                  </div>
                )}
                {!showSubtasks && taskSubtasks && taskSubtasks.length > 0 && (
                  <button
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer select-none"
                    onClick={(e) => { e.stopPropagation(); setLocalExpanded(v => !v); }}
                    data-testid={`button-expand-subtasks-${task.id}`}
                  >
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${localExpanded ? "rotate-180" : ""}`} />
                    <ListTodo className="h-3 w-3" />
                    {getSubTaskProgress && (() => {
                      const progress = getSubTaskProgress(task.id);
                      if (progress && progress.total > 0) {
                        return <span>{progress.total} sub-task{progress.total !== 1 ? "s" : ""} &mdash; {progress.completed}/{progress.total} done</span>;
                      }
                      return <span>{taskSubtasks.length} sub-task{taskSubtasks.length !== 1 ? "s" : ""}</span>;
                    })()}
                  </button>
                )}
              </div>
            </div>
          </div>
          {(showSubtasks || localExpanded) && (
            <InlineSubtaskPanel
              taskId={task.id}
              readOnly={!!task.isShared && task.sharedPermission !== "edit"}
              subtasks={taskSubtasks ?? []}
              onToggle={onSubtaskToggle}
              onDateChange={onSubtaskDateChange}
              onTextChange={onSubtaskTextChange}
              onStar={onSubtaskStar}
              onReorder={onSubtaskReorder}
              onDelete={onSubtaskDelete}
              hideCompleted={hideCompleted}
              onToggleHideCompleted={onToggleHideCompleted}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function TaskCard({ task, isOverdue, getLinkedItemName, getSubTaskProgress, toggleTaskMutation, toggleTopThreeMutation, setViewingTask, setCreateNoteForTask, setEditingTask, setDeletingTaskId, selectedTasks, toggleTaskSelection, onShareTask, quickProgressPopoverId, setQuickProgressPopoverId, quickProgressPopoverNote, setQuickProgressPopoverNote, quickProgressMutation, onScheduleTask, highlightAssigned = false }: any) {
  return (
    <Card
      className={`transition-all hover:shadow-md cursor-pointer ${
        highlightAssigned ? assignedTaskCardClass : isOverdue(task) ? "border-red-300 bg-red-50 dark:bg-red-950/20" : ""
      } ${selectedTasks.has(task.id) ? "ring-2 ring-primary" : ""}`}
      data-testid={`task-card-${task.id}`}
      onClick={() => setEditingTask(task)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="mt-1 flex items-center" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={selectedTasks.has(task.id)}
              onChange={() => toggleTaskSelection(task.id)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer accent-primary"
              data-testid={`checkbox-select-task-${task.id}`}
            />
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={task.completed}
              onCheckedChange={() => toggleTaskMutation.mutate({ id: task.id, completed: !task.completed })}
              className="mt-1"
              data-testid={`checkbox-task-${task.id}`}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                <h3
                  className={`font-medium text-foreground cursor-pointer hover:text-primary transition-colors truncate ${
                    task.completed ? "line-through text-muted-foreground" : ""
                  }`}
                  data-testid={`task-title-${task.id}`}
                >
                  {task.title}
                </h3>
                {task.isShared && task.sharedBy && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 flex-shrink-0">
                    <Users className="h-3 w-3" />
                    Shared by {task.sharedBy.firstName}
                  </span>
                )}
                {getSubTaskProgress && (() => {
                  const progress = getSubTaskProgress(task.id);
                  if (progress && progress.total > 0) {
                    return (
                      <div className="flex-1 max-w-[200px] flex items-center gap-2 flex-shrink-0" data-testid={`task-progress-bar-${task.id}`}>
                        <Progress value={progress.percentage} className="h-2" />
                        <span className="text-xs text-muted-foreground whitespace-nowrap">{progress.percentage}%</span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
              <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                {/* Desktop: show all buttons */}
                <div className="hidden md:flex items-center gap-1">
                  <Popover 
                    open={quickProgressPopoverId === task.id} 
                    onOpenChange={(open) => {
                      setQuickProgressPopoverId(open ? task.id : null);
                      if (!open) setQuickProgressPopoverNote("");
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 sm:h-8 sm:w-8"
                        data-testid={`button-quick-progress-${task.id}`}
                        title="Add Quick Progress"
                      >
                        <MessageSquare className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80" onClick={(e) => e.stopPropagation()}>
                      <div className="space-y-3">
                        <div className="font-medium text-sm">Quick Progress for: {task.title}</div>
                        <Textarea
                          placeholder="Enter progress notes..."
                          value={quickProgressPopoverNote}
                          onChange={(e) => setQuickProgressPopoverNote(e.target.value)}
                          className="min-h-[80px]"
                          data-testid={`textarea-quick-progress-${task.id}`}
                        />
                        <Button
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            if (quickProgressPopoverNote.trim()) {
                              quickProgressMutation.mutate({
                                taskId: task.id,
                                notes: quickProgressPopoverNote.trim(),
                              });
                            }
                          }}
                          disabled={!quickProgressPopoverNote.trim() || quickProgressMutation.isPending}
                          data-testid={`button-submit-quick-progress-${task.id}`}
                        >
                          {quickProgressMutation.isPending ? "Saving..." : "Add Progress"}
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                  <TaskAttachmentsDialog
                    taskId={task.id}
                    trigger={
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 sm:h-8 sm:w-8"
                        data-testid={`button-view-documents-${task.id}`}
                        title="View Documents"
                      >
                        <Paperclip className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </Button>
                    }
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 sm:h-8 sm:w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTopThreeMutation.mutate(task.id);
                    }}
                    data-testid={`button-toggle-top-three-${task.id}`}
                    title={task.isTopThree ? "Remove from Top Three" : "Add to Top Three"}
                  >
                    <Star 
                      className="h-3.5 w-3.5 sm:h-4 sm:w-4" 
                      fill={task.isTopThree ? "#f59e0b" : "none"}
                      style={{ color: task.isTopThree ? "#f59e0b" : "currentColor" }}
                    />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 sm:h-8 sm:w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCreateNoteForTask(task);
                    }}
                    data-testid={`button-create-note-task-${task.id}`}
                    title="Create Note"
                  >
                    <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 sm:h-8 sm:w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      onShareTask(task);
                    }}
                    data-testid={`button-share-task-${task.id}`}
                    title="Share Task"
                  >
                    <Share2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 sm:h-8 sm:w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingTask(task);
                    }}
                    data-testid={`button-edit-task-${task.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 sm:h-8 sm:w-8 text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeletingTaskId(task.id);
                    }}
                    data-testid={`button-delete-task-${task.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </Button>
                </div>
                {/* Mobile: dropdown menu */}
                <div className="md:hidden">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`button-task-actions-mobile-card-${task.id}`}>
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem onClick={() => setEditingTask(task)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => toggleTopThreeMutation.mutate(task.id)}>
                        <Star className="h-4 w-4 mr-2" fill={task.isTopThree ? "#f59e0b" : "none"} />
                        {task.isTopThree ? "Unstar" : "Star"}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setCreateNoteForTask(task)}>
                        <FileText className="h-4 w-4 mr-2" />
                        Create Note
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onShareTask(task)}>
                        <Share2 className="h-4 w-4 mr-2" />
                        Share
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { if (onScheduleTask) onScheduleTask(task); }}>
                        <CalendarPlus className="h-4 w-4 mr-2" />
                        Add to Calendar
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeletingTaskId(task.id)} className="text-destructive">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
            {task.description && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2" data-testid={`task-description-${task.id}`}>
                {task.description}
              </p>
            )}
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <div className="flex items-center gap-1 text-xs text-muted-foreground" data-testid={`task-category-${task.id}`}>
                <Tag className="h-3 w-3" />
                {sanitizeCategory(task.category) || "Uncategorized"}
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground" data-testid={`task-priority-${task.id}`}>
                <AlertCircle className="h-3 w-3" />
                Priority: {task.priority || "medium"}
              </div>
              {task.dueDate && (
                <div className={`flex items-center gap-1 text-xs ${
                  isOverdue(task) ? "text-red-600 font-medium" : "text-muted-foreground"
                }`} data-testid={`task-due-date-${task.id}`}>
                  <Calendar className="h-3 w-3" />
                  Due: {format(new Date(task.dueDate), "MMM d, yyyy")}
                  {isOverdue(task) && <span className="ml-1">(Overdue)</span>}
                </div>
              )}
              {getLinkedItemName && getLinkedItemName(task) && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground" data-testid={`task-link-${task.id}`}>
                  <LinkIcon className="h-3 w-3" />
                  <span>{getLinkedItemName(task)?.type}:</span>
                  <span className="font-medium">{getLinkedItemName(task)?.name}</span>
                </div>
              )}
              {getSubTaskProgress && (() => {
                const progress = getSubTaskProgress(task.id);
                if (progress) {
                  return (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground" data-testid={`task-subtask-progress-${task.id}`}>
                      <ListTodo className="h-3 w-3" />
                      <span>{progress.total} sub-tasks, {progress.percentage}% completed ({progress.total}/{progress.completed})</span>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Shared Task Card - for tasks shared with the current user
export function SharedTaskCard({ sharedTask, updateSharedTaskMutation, toast }: { 
  sharedTask: any; 
  updateSharedTaskMutation: any;
  toast: any;
}) {
  const task = sharedTask.task;
  const canEdit = sharedTask.permission === 'edit';
  const owner = sharedTask.owner;

  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(task.title ?? "");
  const [editDescription, setEditDescription] = useState(task.description ?? "");
  // Use formatDateInput (local-timezone YYYY-MM-DD) so east-of-UTC users don't
  // see their date silently shifted back one day on open (toISOString returns UTC).
  const [editDueDate, setEditDueDate] = useState(
    task.dueDate ? formatDateInput(new Date(task.dueDate)) : ""
  );

  const handleCheckboxChange = () => {
    if (!canEdit) {
      toast({
        title: "View Only",
        description: "You don't have permission to complete this task",
        variant: "destructive",
      });
      return;
    }
    updateSharedTaskMutation.mutate({ 
      taskId: sharedTask.taskId, 
      completed: !task.completed 
    });
  };

  const handleEditOpen = () => {
    setEditTitle(task.title ?? "");
    setEditDescription(task.description ?? "");
    // Same local-date formatting on re-open so the displayed date is stable.
    setEditDueDate(task.dueDate ? formatDateInput(new Date(task.dueDate)) : "");
    setIsEditing(true);
  };

  const handleEditSave = () => {
    if (!editTitle.trim()) return;
    // Convert local date string (YYYY-MM-DD) to local-midnight ISO instant
    // so users west of UTC don't get an off-by-one day.
    const dueDateIso: string | null = editDueDate ? parseLocalDate(editDueDate).toISOString() : null;
    updateSharedTaskMutation.mutate(
      {
        taskId: sharedTask.taskId,
        title: editTitle.trim(),
        description: editDescription.trim() || null,
        dueDate: dueDateIso,
      },
      {
        onSuccess: () => setIsEditing(false),
        onError: () => {
          toast({
            title: "Save failed",
            description: "Could not save changes. Please try again.",
            variant: "destructive",
          });
        },
      }
    );
  };

  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && !task.completed;
  
  return (
    <Card
      className={`transition-all hover:shadow-md ${
        isOverdue ? "border-red-300 bg-red-50 dark:bg-red-950/20" : ""
      }`}
      data-testid={`shared-task-card-${task.id}`}
    >
      <CardContent className="p-4">
        {isEditing ? (
          <div className="space-y-3" data-testid={`shared-task-edit-form-${task.id}`}>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Title</label>
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="mt-1"
                data-testid={`shared-task-edit-title-${task.id}`}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Description</label>
              <Textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="mt-1 resize-none"
                rows={2}
                data-testid={`shared-task-edit-description-${task.id}`}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Due date</label>
              <Input
                type="date"
                value={editDueDate}
                onChange={(e) => setEditDueDate(e.target.value)}
                className="mt-1"
                data-testid={`shared-task-edit-due-date-${task.id}`}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(false)}
                disabled={updateSharedTaskMutation.isPending}
                data-testid={`shared-task-edit-cancel-${task.id}`}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleEditSave}
                disabled={!editTitle.trim() || updateSharedTaskMutation.isPending}
                data-testid={`shared-task-edit-save-${task.id}`}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={task.completed}
                onCheckedChange={handleCheckboxChange}
                className={`mt-1 ${!canEdit ? 'opacity-50' : ''}`}
                disabled={updateSharedTaskMutation.isPending}
                data-testid={`checkbox-shared-task-${task.id}`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h3
                  className={`font-medium text-foreground ${
                    task.completed ? "line-through text-muted-foreground" : ""
                  }`}
                  data-testid={`shared-task-title-${task.id}`}
                >
                  {task.title}
                </h3>
                <div className="flex items-center gap-1">
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={handleEditOpen}
                      data-testid={`shared-task-edit-button-${task.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Badge variant={canEdit ? "default" : "secondary"} className="text-xs">
                    {canEdit ? "Can Edit" : "View Only"}
                  </Badge>
                </div>
              </div>
              {task.description && (
                <p className="text-sm text-muted-foreground mt-1" data-testid={`shared-task-description-${task.id}`}>
                  {task.description}
                </p>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                <Badge variant="outline" className="text-xs" data-testid={`shared-task-category-${task.id}`}>
                  {sanitizeCategory(task.category) || "Uncategorized"}
                </Badge>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Flag className="h-3 w-3" />
                  Priority: {task.priority || "medium"}
                </div>
                {task.dueDate && (
                  <div className={`flex items-center gap-1 text-xs ${
                    isOverdue ? "text-red-600 font-medium" : "text-muted-foreground"
                  }`} data-testid={`shared-task-due-date-${task.id}`}>
                    <Calendar className="h-3 w-3" />
                    Due: {format(new Date(task.dueDate), "MMM d, yyyy")}
                    {isOverdue && <span className="ml-1">(Overdue)</span>}
                  </div>
                )}
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Users className="h-3 w-3" />
                  Shared by: {owner?.firstName} {owner?.lastName}
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function GroupedTasksList({ groups, collapsedGroups, toggleGroupCollapsed, handleDragEnd, sensors, isOverdue, getLinkedItemName, getSubTaskProgress, toggleTaskMutation, toggleTopThreeMutation, setViewingTask, setCreateNoteForTask, setEditingTask, setDeletingTaskId, selectedTasks, toggleTaskSelection, onShareTask, quickProgressPopoverId, setQuickProgressPopoverId, quickProgressPopoverNote, setQuickProgressPopoverNote, quickProgressMutation, showSubtasks, subTasksByTaskId, onSubtaskToggle, onSubtaskStar, onSubtaskDateChange, onSubtaskTextChange, onSubtaskReorder, onSubtaskDelete, getHideCompleted, onToggleHideCompleted, onScheduleTask, viewerId, isAssistantViewer, parentPath = "" }: any) {
  return (
    <div className="space-y-4">
      {groups.map((group: any, index: number) => {
        const groupPath = parentPath ? `${parentPath}/${group.groupName}` : group.groupName;
        const isCollapsed = collapsedGroups.has(groupPath);
        const taskCount = group.tasks.length + (group.subGroups || []).reduce((sum: number, sg: any) => {
          const countTasks = (g: any): number => {
            return g.tasks.length + (g.subGroups || []).reduce((s: number, subg: any) => s + countTasks(subg), 0);
          };
          return sum + countTasks(sg);
        }, 0);

        return (
          <div key={groupPath} className="border border-border rounded-lg overflow-hidden">
            <Collapsible open={!isCollapsed}>
              <CollapsibleTrigger
                onClick={() => toggleGroupCollapsed(groupPath)}
                className="w-full px-4 py-3 bg-muted/50 hover:bg-muted/70 transition-colors flex items-center justify-between cursor-pointer"
                data-testid={`group-header-${groupPath}`}
              >
                <div className="flex items-center gap-2">
                  {isCollapsed ? (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                  <h3 className="font-semibold text-foreground">{typeof group.groupName === "string" ? group.groupName : String(group.groupName ?? "Unknown")}</h3>
                  <Badge variant="secondary">{taskCount}</Badge>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4">
                  {group.tasks.length > 0 && (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={(event) => handleDragEnd(event, group.tasks)}
                    >
                      <SortableContext
                        items={group.tasks.map((t: Task) => t.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-3">
                          {group.tasks.map((task: Task) => (
                            <SortableTaskItem
                              key={task.id}
                              task={task}
                              highlightAssigned={isAssignedToAssistant(task, viewerId, isAssistantViewer)}
                              isOverdue={isOverdue}
                              getLinkedItemName={getLinkedItemName}
                              getSubTaskProgress={getSubTaskProgress}
                              toggleTaskMutation={toggleTaskMutation}
                              toggleTopThreeMutation={toggleTopThreeMutation}
                              setViewingTask={setViewingTask}
                              setCreateNoteForTask={setCreateNoteForTask}
                              setEditingTask={setEditingTask}
                              setDeletingTaskId={setDeletingTaskId}
                              selectedTasks={selectedTasks}
                              toggleTaskSelection={toggleTaskSelection}
                              onShareTask={onShareTask}
                              quickProgressPopoverId={quickProgressPopoverId}
                              setQuickProgressPopoverId={setQuickProgressPopoverId}
                              quickProgressPopoverNote={quickProgressPopoverNote}
                              setQuickProgressPopoverNote={setQuickProgressPopoverNote}
                              quickProgressMutation={quickProgressMutation}
                              showSubtasks={showSubtasks}
                              taskSubtasks={subTasksByTaskId ? (subTasksByTaskId.get(task.id) || []) : []}
                              onSubtaskToggle={onSubtaskToggle}
                              onSubtaskStar={onSubtaskStar}
                              onSubtaskDateChange={onSubtaskDateChange}
                              onSubtaskTextChange={onSubtaskTextChange}
                              onSubtaskReorder={onSubtaskReorder}
                              onSubtaskDelete={onSubtaskDelete}
                              hideCompleted={getHideCompleted ? getHideCompleted(task.id) : false}
                              onToggleHideCompleted={onToggleHideCompleted}
                              onScheduleTask={onScheduleTask}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                  {group.subGroups && group.subGroups.length > 0 && (
                    <div className={group.tasks.length > 0 ? "mt-4" : ""}>
                      <GroupedTasksList
                        viewerId={viewerId}
                        isAssistantViewer={isAssistantViewer}
                        groups={group.subGroups}
                        collapsedGroups={collapsedGroups}
                        toggleGroupCollapsed={toggleGroupCollapsed}
                        handleDragEnd={handleDragEnd}
                        sensors={sensors}
                        isOverdue={isOverdue}
                        getLinkedItemName={getLinkedItemName}
                        getSubTaskProgress={getSubTaskProgress}
                        toggleTaskMutation={toggleTaskMutation}
                        toggleTopThreeMutation={toggleTopThreeMutation}
                        setViewingTask={setViewingTask}
                        setCreateNoteForTask={setCreateNoteForTask}
                        setEditingTask={setEditingTask}
                        setDeletingTaskId={setDeletingTaskId}
                        selectedTasks={selectedTasks}
                        toggleTaskSelection={toggleTaskSelection}
                        onShareTask={onShareTask}
                        quickProgressPopoverId={quickProgressPopoverId}
                        setQuickProgressPopoverId={setQuickProgressPopoverId}
                        quickProgressPopoverNote={quickProgressPopoverNote}
                        setQuickProgressPopoverNote={setQuickProgressPopoverNote}
                        quickProgressMutation={quickProgressMutation}
                        showSubtasks={showSubtasks}
                        subTasksByTaskId={subTasksByTaskId}
                        onSubtaskToggle={onSubtaskToggle}
                        onSubtaskStar={onSubtaskStar}
                        onSubtaskDateChange={onSubtaskDateChange}
                        onSubtaskTextChange={onSubtaskTextChange}
                        onSubtaskReorder={onSubtaskReorder}
                        onSubtaskDelete={onSubtaskDelete}
                        getHideCompleted={getHideCompleted}
                        onToggleHideCompleted={onToggleHideCompleted}
                        onScheduleTask={onScheduleTask}
                        parentPath={groupPath}
                      />
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        );
      })}
    </div>
  );
}

